import { useEffect, useRef, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    Box,
    Grid,
    TextField,
    MenuItem,
    Typography,
    Button,
    IconButton,
    FormHelperText,
} from '@mui/material';
import { Delete, InsertDriveFile, Opacity, UploadFile as UploadFileIcon } from '@mui/icons-material';
import { useCommonItems } from '../../../../../../src/contexts/CommonItensProvider';
import { CommonDialogActionsForm } from '../../../../../../src/components/common/CommonDialogActionsForm';
import { getFormErrorMessage, fillInputErrors } from '../configs/functions';
import { updateContrato, createContrato } from '../../../management/contract/functions';

const MAX_FILE_SIZE = 5 * 1024 * 1024;
const ACCEPTED_EXTENSIONS = ['pdf', 'doc', 'docx'];

const isAtivo = (status) => status === 1 || status === true || status === 'ativa';

const toInputDate = (date) => {
    if (!date) return '';
    const value = String(date).slice(0, 10);
    const match = value.match(/^(\d{2})[-/](\d{2})[-/](\d{4})$/);
    return match ? `${match[3]}-${match[2]}-${match[1]}` : value;
};

const formatDateForApi = (date) => {
    if (!date) return '';
    const [year, month, day] = date.split('-');
    if (!year || !month || !day) return date;
    return `${day}-${month}-${year}`;
};

export function ContractForm({
                                 open,
                                 setOpen,
                                 contrato,
                                 actionsAllowed = true,
                                 onSuccess,
                                 visualizar = false
                             }) {
    const { exibirDialog, handleCloseDialog, exibirAlerta } = useCommonItems();

    const [isLoading, setIsLoading] = useState(false);
    const [selectedFile, setSelectedFile] = useState(null);
    const [fileError, setFileError] = useState('');
    const [isDragging, setIsDragging] = useState(false);
    const fileInputRef = useRef(null);

    const isEdit = Boolean(contrato?.id && !visualizar);
    const isVisualizar = Boolean(contrato?.id && visualizar);

    const {
        register,
        handleSubmit,
        reset,
        setError,
        control,
        formState: { errors, dirtyFields },
    } = useForm();

    useEffect(() => {
        if (!open) {
            reset();
            setSelectedFile(null);
            setFileError('');
            setIsDragging(false);
            return;
        }

        if (contrato?.id) {
            reset({
                nome: contrato.nome ?? '',
                status: isAtivo(contrato.status) ? 1 : 0,
                inicio_contrato: toInputDate(contrato.inicio_contrato),
                fim_contrato: toInputDate(contrato.fim_contrato),
                mensalidade: contrato.mensalidade ?? '',
                qtd_colaboradores: contrato.qtd_colaboradores ?? 1,
                qtd_colaboradores_admin: contrato.qtd_colaboradores_admin ?? 0,
            });
        } else {
            reset({
                nome: '',
                inicio_contrato: '',
                fim_contrato: '',
                mensalidade: '',
                qtd_empresas: 1,
                qtd_colaboradores: 1,
                qtd_colaboradores_admin: 1,
            });
        }
    }, [open, contrato, reset]);

    function selectFile(file) {
        if (!file) return;

        const ext = file.name.split('.').pop()?.toLowerCase();

        if (!ACCEPTED_EXTENSIONS.includes(ext)) {
            setFileError('Formato inválido. Use PDF, DOC ou DOCX.');
            return;
        }

        if (file.size > MAX_FILE_SIZE) {
            setFileError('O arquivo deve ter no máximo 5 MB');
            return;
        }

        setFileError('');
        setSelectedFile(file);
    }

    const handleFileChange = (e) => {
        selectFile(e.target.files?.[0]);
        e.target.value = '';
    };

    const handleDragOver = (e) => {
        e.preventDefault();
        setIsDragging(true);
    };

    const handleDragLeave = () => setIsDragging(false);

    const handleDrop = (e) => {
        e.preventDefault();
        setIsDragging(false);
        selectFile(e.dataTransfer.files?.[0]);
    };

    const handleRemoveFile = (e) => {
        e.stopPropagation();
        setSelectedFile(null);
        setFileError('');
    };

    const buildUpdatePayload = (data) => {
        const dirtyData = Object.keys(dirtyFields).reduce((acc, key) => {
            if (Object.prototype.hasOwnProperty.call(data, key)) {
                acc[key] = data[key];
            }
            return acc;
        }, {});

        if (dirtyData.inicio_contrato) {
            dirtyData.inicio_contrato = formatDateForApi(dirtyData.inicio_contrato);
        }

        if (dirtyData.fim_contrato) {
            dirtyData.fim_contrato = formatDateForApi(dirtyData.fim_contrato);
        }

        if (Object.prototype.hasOwnProperty.call(dirtyData, 'status')) {
            const ativo = Number(dirtyData.status) === 1;
            dirtyData.status =
                typeof contrato.status === 'number'
                    ? ativo ? 1 : 0
                    : ativo ? 'ativa' : 'inativa';
        }

        return { id: contrato.id, ...dirtyData };
    };

    const buildCreatePayload = (data) => {
        const payload = new FormData();

        payload.append('nome', data.nome);
        payload.append('qtd_empresas', data.qtd_empresas);
        payload.append('qtd_colaboradores', data.qtd_colaboradores);
        payload.append('qtd_colaboradores_admin', data.qtd_colaboradores_admin);
        payload.append('inicio_contrato', formatDateForApi(data.inicio_contrato));
        payload.append('fim_contrato', formatDateForApi(data.fim_contrato));
        payload.append('renovacao_automatica', 0);
        payload.append('mensalidade', Number.isFinite(data.mensalidade) ? data.mensalidade : 0);

        if (selectedFile) {
            payload.append('contrato', selectedFile);
        }

        return payload;
    };

    const onSubmit = async (data) => {
        if (fileError) return;

        setIsLoading(true);
        exibirDialog(isEdit ? 'Salvando contrato. Aguarde...' : 'Cadastrando contrato. Aguarde...');

        try {
            if (isEdit) {
                await updateContrato(buildUpdatePayload(data));
            } else {
                await createContrato(buildCreatePayload(data));
            }

            setOpen(false);

            exibirAlerta(
                'Sucesso',
                isEdit ? 'Contrato atualizado com sucesso' : 'Contrato cadastrado com sucesso',
                'success'
            );

            onSuccess?.();
        } catch (error) {
            const errorMessage =
                error?.response?.data?.error ||
                error?.response?.data?.message ||
                (isEdit ? 'Erro ao atualizar contrato.' : 'Erro ao cadastrar contrato.');

            fillInputErrors(error, setError);
            exibirAlerta('Erro', errorMessage, 'error');
        } finally {
            setIsLoading(false);
            handleCloseDialog();
        }
    };

    return (
        <Dialog
            open={open}
            onClose={() => !isLoading && setOpen(false)}
            fullWidth
            maxWidth="md"
        >
            <form onSubmit={handleSubmit(onSubmit)}>
                <DialogTitle>
                    {isEdit ? 'Editar Contrato': isVisualizar ? 'Visualizar Contrato' :'Cadastrar Contrato'}
                </DialogTitle>

                <DialogContent>
                    <Box sx={{ p: 2, backgroundColor: '#fafafa' }}>
                        <Grid container spacing={2}>
                            <Grid item xs={12}>
                        <TextField
                            fullWidth
                            label="Nome do Contrato"
                            placeholder="Digite o nome do contrato"
                            InputLabelProps={{ shrink: true }}
                            {...register('nome', {
                                required: 'Informe o nome do contrato',
                            })}
                            error={!!errors.nome}
                            helperText={getFormErrorMessage(errors, 'nome')}
                            disabled={isVisualizar}
                            sx={{
                                "& .MuiInputBase-input.Mui-disabled": {
                                    WebkitTextFillColor: "#000000",
                                    opacity: 1,
                                },
                                "& .MuiInputLabel-root.Mui-disabled": {
                                    opacity: 1,
                                }
                            }}
                        />

                            </Grid>

                            {isEdit && isVisualizar && (
                                <Grid item xs={12} md={4}>
                                    <Controller
                                        name="status"
                                        control={control}
                                        defaultValue={1}
                                        rules={{ required: 'Informe a situação' }}
                                        render={({ field }) => (
                                            <TextField
                                                select
                                                fullWidth
                                                label="Situação"
                                                InputLabelProps={{ shrink: true }}
                                                {...field}
                                                error={!!errors.status}
                                                helperText={getFormErrorMessage(errors, 'status')}
                                               disabled = {isVisualizar ? true : false}
                                            >
                                                <MenuItem value={1}>Ativo</MenuItem>
                                                <MenuItem value={0}>Inativo</MenuItem>


                                            </TextField>
                                        )}
                                    />
                                </Grid>
                            )}

                            <Grid item xs={12} md={4}>
                                <TextField
                                    fullWidth
                                    type="date"
                                    label="Início do Contrato"
                                    InputLabelProps={{ shrink: true }}
                                    {...register('inicio_contrato', {
                                        required: 'Informe a data de início',
                                    })}
                                    error={!!errors.inicio_contrato}
                                    helperText={getFormErrorMessage(errors, 'inicio_contrato')}
                                    disabled = {isVisualizar ? true : false}

                                                            sx={{
                                "& .MuiInputBase-input.Mui-disabled": {
                                    WebkitTextFillColor: "#000000",
                                    opacity: 1,
                                },
                                "& .MuiInputLabel-root.Mui-disabled": {
                                    opacity: 1,
                                }
                            }}
                                />
                            </Grid>

                            <Grid item xs={12} md={4}>
                                <TextField
                                    fullWidth
                                    type="date"
                                    label="Fim do Contrato"
                                    InputLabelProps={{ shrink: true }}
                                    {...register('fim_contrato', {
                                        required: 'Informe a data de término',
                                    })}
                                    error={!!errors.fim_contrato}
                                    helperText={getFormErrorMessage(errors, 'fim_contrato')}
                                    disabled = {isVisualizar ? true : false}
                                                            sx={{
                                "& .MuiInputBase-input.Mui-disabled": {
                                    WebkitTextFillColor: "#000000",
                                    opacity: 1,
                                },
                                "& .MuiInputLabel-root.Mui-disabled": {
                                    opacity: 1,
                                }
                            }}
                                />
                            </Grid>

                            <Grid item xs={12} md={4}>
                                <TextField
                                    fullWidth
                                    type="number"
                                    label="Mensalidade"
                                    placeholder="0,00"
                                    InputLabelProps={{ shrink: true }}
                                    inputProps={{ min: 0, step: '0.01' }}
                                    {...register('mensalidade', {
                                        required: isEdit ? 'Informe a mensalidade' : false,
                                        valueAsNumber: true,
                                        min: {
                                            value: 0,
                                            message: 'A mensalidade não pode ser negativa',
                                        },
                                    })}
                                    error={!!errors.mensalidade}
                                    helperText={getFormErrorMessage(errors, 'mensalidade')}
                                    disabled = {isVisualizar ? true : false}

                                                            sx={{
                                "& .MuiInputBase-input.Mui-disabled": {
                                    WebkitTextFillColor: "#000000",
                                    opacity: 1,
                                },
                                "& .MuiInputLabel-root.Mui-disabled": {
                                    opacity: 1,
                                }
                            }}
                                />
                            </Grid>

                            {!isEdit && !isVisualizar && (
                                <Grid item xs={12} md={4}>
                                    <TextField
                                        fullWidth
                                        type="number"
                                        label="Empresas"
                                        placeholder="1"
                                        InputLabelProps={{ shrink: true }}
                                        inputProps={{ min: 1, step: 1 }}
                                        {...register('qtd_empresas', {
                                            required: 'Informe a quantidade de empresas',
                                            valueAsNumber: true,
                                            min: { value: 1, message: 'O mínimo é 1' },
                                        })}
                                        error={!!errors.qtd_empresas}
                                        helperText={getFormErrorMessage(errors, 'qtd_empresas')}
                                    />
                                </Grid>
                            )}

                            <Grid item xs={12} md={4}>
                                <TextField
                                    fullWidth
                                    type="number"
                                    label="Colaboradores"
                                    placeholder="0"
                                    InputLabelProps={{ shrink: true }}
                                    inputProps={{ min: isEdit ? 0 : 1, step: 1 }}
                                    {...register('qtd_colaboradores', {
                                        required: 'Informe a quantidade de colaboradores',
                                        valueAsNumber: true,
                                        min: {
                                            value: isEdit ? 0 : 1,
                                            message: isEdit ? 'O valor não pode ser negativo' : 'O mínimo é 1',
                                        },
                                    })}
                                    error={!!errors.qtd_colaboradores}
                                    helperText={getFormErrorMessage(errors, 'qtd_colaboradores')}
                                    disabled = {isVisualizar ? true : false}

                                                                sx={{
                                "& .MuiInputBase-input.Mui-disabled": {
                                    WebkitTextFillColor: "#000000",
                                    opacity: 1,
                                },
                                "& .MuiInputLabel-root.Mui-disabled": {
                                    opacity: 1,
                                }
                            }}
                                />
                            </Grid>

                            <Grid item xs={12} md={4}>
                                <TextField
                                    fullWidth
                                    type="number"
                                    label="Usuários Administradores"
                                    placeholder="0"
                                    InputLabelProps={{ shrink: true }}
                                    inputProps={{ min: isEdit ? 0 : 1, step: 1 }}
                                    {...register('qtd_colaboradores_admin', {
                                        required: 'Informe a quantidade de administradores',
                                        valueAsNumber: true,
                                        min: {
                                            value: isEdit ? 0 : 1,
                                            message: isEdit ? 'O valor não pode ser negativo' : 'O mínimo é 1',
                                        },
                                    })}
                                    error={!!errors.qtd_colaboradores_admin}
                                    helperText={getFormErrorMessage(errors, 'qtd_colaboradores_admin')}
                                    disabled = {isVisualizar ? true : false}

                                                            sx={{
                                "& .MuiInputBase-input.Mui-disabled": {
                                    WebkitTextFillColor: "#000000",
                                    opacity: 1,
                                },
                                "& .MuiInputLabel-root.Mui-disabled": {
                                    opacity: 1,
                                }
                            }}
                                />
                            </Grid>

                            {!isEdit &&  !isVisualizar &&(
                                <Grid item xs={12}>
                                    <Box
                                        onClick={() => fileInputRef.current?.click()}
                                        onDragOver={handleDragOver}
                                        onDragLeave={handleDragLeave}
                                        onDrop={handleDrop}
                                        sx={{
                                            border: '2px dashed',
                                            borderColor: fileError
                                                ? 'error.main'
                                                : isDragging
                                                    ? 'primary.main'
                                                    : 'grey.400',
                                            borderRadius: 2,
                                            p: 4,
                                            textAlign: 'center',
                                            cursor: 'pointer',
                                            position: 'relative',
                                            backgroundColor: isDragging || selectedFile ? 'action.hover' : 'background.paper',
                                            transition: 'all 0.2s',
                                            '&:hover': { borderColor: 'primary.main' },
                                        }}
                                    >
                                        {selectedFile && (
                                            <IconButton
                                                onClick={handleRemoveFile}
                                                sx={{ position: 'absolute', top: 8, right: 8, color: 'grey.600' }}
                                            >
                                                <Delete />
                                            </IconButton>
                                        )}

                                        {selectedFile ? (
                                            <>
                                                <InsertDriveFile color="primary" sx={{ fontSize: 40 }} />
                                                <Typography variant="body1" sx={{ mt: 1, wordBreak: 'break-word' }}>
                                                    {selectedFile.name}
                                                </Typography>
                                                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 2 }}>
                                                    {(selectedFile.size / 1024 / 1024).toFixed(2)} MB
                                                </Typography>
                                                <Button variant="outlined">Alterar Arquivo</Button>
                                            </>
                                        ) : (
                                            <>
                                                <UploadFileIcon color="primary" sx={{ fontSize: 40 }} />
                                                <Typography variant="body1" sx={{ mt: 1 }}>
                                                    Arraste ou clique para adicionar o contrato
                                                </Typography>
                                                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 2 }}>
                                                    PDF, DOC ou DOCX até 5 MB (opcional)
                                                </Typography>
                                                <Button variant="contained">Selecionar Arquivo</Button>
                                            </>
                                        )}

                                        <input
                                            type="file"
                                            hidden
                                            ref={fileInputRef}
                                            onChange={handleFileChange}
                                            accept=".pdf,.doc,.docx"
                                        />
                                    </Box>

                                    {fileError && <FormHelperText error>{fileError}</FormHelperText>}
                                </Grid>
                            )}
                        </Grid>
                    </Box>
                </DialogContent>

                <CommonDialogActionsForm
                    errors={errors}
                    isLoading={isLoading}
                    setOpen={setOpen}
                    disableSubmit={true}
                />
            </form>
        </Dialog>
    );
}